import { CrudAccess, EmployeeIdentityResponse, EmployeeModel, IcdOfficeEmployeeModel, BankInfoResponse } from "@herbjoy/apex-api-models";
import { IFormController, IPromise, ITimeoutService } from "angular";
import { EventEmitter } from "../../../../common/components/event-emitter";
import { FeatureManager } from "../../../../core/feature-flags/feature-manager.service";
import { ROSTER_SEPARATE_EDIT_PAGES } from "../../roster-flags";
import { EmploymentRecordEditContext, EmploymentRecordHybridModel, EmploymentRecordInputModel } from "./employment-record-edit-context.factory";
import templateUrl from "./employment-record-list-editor.component.html";

export type EmployeeViewModel = Omit<EmployeeModel, "IcdOffices"> & {
    IcdOffices: Partial<EmploymentRecordInputModel>[];
}

interface SaveEvent {
    $event: {
        employee: EmployeeViewModel;
        employeeBankInfo: BankInfoResponse;
        onModelStateError(modelState: any): void;
    };
}

type OnSaveFn = (event: SaveEvent) => IPromise<any>;

/**
 * A component embedding a list of editable employment records. Validation
 * occurs for all records and the whole employment record must be valid in order
 * to save.
 */
export const EmploymentRecordListEditorComponent = {
    templateUrl,
    require: {
        parentForm: "^^?form"
    },
    bindings: {
        /** The employee whose employment record is being edited. */
        employee: "<",
        employeeBankInfo: "<",
        /** The employment record edit context providing options for dropdowns, etc. */
        context: "<",
        /** The identity of the current user. */
        identity: "<",
        /** Permissions representing user's role ability to edit the employee */
        roleAccess: "<",
        /** Permission representing whether the user can process payrolls */
        payrollAccess: "<",
        /** Indicates whether the edited employee is self-onboarding */
        isSelfSignUp: "<",
        /** Event to be emitted when the user requests viewing the minimum wage requirements at an office. */
        showMinimumWageRequirements: "&onShowMinimumWageRequirements",
        save: "&onSave",
        initOnboarding: "&onInitOnboarding",
        revokeOnboarding: "&onRevokeOnboarding",
        featureManager: "<featureManagerSync"
    },
    controller: class {
        //@Input
        employee: EmployeeViewModel;
        //@Input
        employeeBankInfo: BankInfoResponse;
        //@Input
        context: EmploymentRecordEditContext;
        //@Input
        identity: EmployeeIdentityResponse;
        //@Input
        roleAccess: CrudAccess;
        //@Input
        payrollAccess: CrudAccess;
        //@Input
        isSelfSignUp: boolean;
        //@Input
        parentForm: IFormController;
        //@Output
        showMinimumWageRequirements: EventEmitter<IcdOfficeEmployeeModel>;
        //@Output
        initOnboarding: EventEmitter<boolean>;
        //@Output
        revokeOnboarding: EventEmitter<boolean>;
        //@Output
        save: OnSaveFn;

        employeeID: number;
        editForm: IFormController;
        featureManager: FeatureManager;
        features: {
            [s: string]: boolean;
        };
        modelState: any;
        constructor(private $timeout: ITimeoutService) {
            'ngInject';
        }

        $onInit() {
            this.features = {};
            this.features.separateEditPages = this.featureManager.isEnabled(ROSTER_SEPARATE_EDIT_PAGES);

            if (!this.parentForm) {
                // Note, parentForm is the parent form, whereas editForm is the local form.
                window.onbeforeunload = () => {
                    if (this.editForm && this.editForm.$dirty) {
                        return "There are unsaved changes. Are you sure you want to abandon them?"
                    }
                }
            }
        }

        $onChanges(changesObj) {
            this.employeeID = this.employee && this.employee.Id || null;
        }

        canAddNewOffice() {
            return this.employee.IcdOffices.every(office => office.HireDate && office.IcdOfficeID);
        }

        addNewOffice() {
            if (!this.canAddNewOffice()) {
                // Adding a new default office would result in a duplicate.
                return;
            }
            const office: Partial<EmploymentRecordInputModel> = {
                EmployeeStatusID: 3,
                EmployeeStatus: this.context.employmentStatuses.filter(function (status) {
                    return status.EmployeeStatusID === 3;
                })[0],
                AdministeredModules: [],
                Roles: [],
                RoleList: [],
                QualifiedPrograms: [],
                Divisions: [],
                TimeClock: true,
                RequireBreak: true,
                IsOnPayroll: true,
                CanDelete: true, // ie, the office is new and deleting it wouldn't cause problems.
                expanded: true
            };
            this.employee.IcdOffices.unshift(office);

            // Re-evaluate validation.
            this.context.changeRole(office as EmploymentRecordHybridModel);

            // Adding the new office doesn't make the employee form dirty until the office form is also made dirty.
            // Force the dirty state change here.
            this.editForm.$setDirty();
            this.$timeout(() => {
                office.icdOfficeEmployeeForm.$setValidity("at-least-one-role", false, undefined);
            })
        }

        removeOffice(i) {
            const office = this.employee.IcdOffices.splice(i, 1)[0];

            // Re-evaluate validation.
            this.context.changeRole(office as EmploymentRecordHybridModel);
        }

        onSave() {
            if (this.editForm && this.editForm.$invalid) {
                alert("Please fix any validation errors and try again.");
                return;
            }
            this.save({
                $event: {
                    employee: this.employee,
                    employeeBankInfo: this.employeeBankInfo,
                    onModelStateError: modelState => {
                        this.modelState = modelState;
                    }
                }
            })
                .then(() => {
                    this.editForm.$setPristine();
                })
                .catch(ex => {
                    this.modelState = ex.data ? ex.data.ModelState : null;
                });
        }
    }
}
